import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useContext, useEffect, type PropsWithChildren } from "react";

import { apiFetch } from "../../lib/api/client";

export type Role = "STUDENT" | "EDUCATOR_ADMIN";

export interface SessionMember {
  id: string;
  username: string;
  registrantName: string;
  role: Role;
  status: "ACTIVE" | "DISABLED";
}

interface SessionContextValue {
  member: SessionMember | null;
  loading: boolean;
  refresh: () => Promise<void>;
}

interface SessionResponse { member: SessionMember }

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: PropsWithChildren) {
  const queryClient = useQueryClient();
  const session = useQuery({
    queryKey: ["session"],
    queryFn: () => apiFetch<SessionResponse>("/auth/session"),
    retry: false,
  });

  useEffect(() => {
    const clear = () => queryClient.setQueryData(["session"], null);
    window.addEventListener("icat:unauthorized", clear);
    return () => window.removeEventListener("icat:unauthorized", clear);
  }, [queryClient]);

  return (
    <SessionContext.Provider
      value={{
        member: session.data?.member ?? null,
        loading: session.isLoading,
        refresh: async () => {
          await session.refetch();
        },
      }}
    >
      {children}
    </SessionContext.Provider>
  );
}

export function useSession(): SessionContextValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error("useSession must be used within SessionProvider");
  return value;
}
